/**
 * Document model knowledge for the diff engine.
 *
 * This is the single source of truth for how the differ classifies HTML
 * elements.  Every tag-set, mark rule, and volatile-attr list lives here
 * so that adding or changing a node type only requires one file edit.
 */
import type { Element } from 'domhandler';

import { TAB_NODE_NAME } from '../tiptap/nodes/tab/constants';
import { TEMPLATE_FIELD_KINDS, templateFieldWrapperClass } from './templateFields';

// ---------------------------------------------------------------------------
// ID attribute
// ---------------------------------------------------------------------------

/** The HTML attribute that carries the stable node identity. */
export const ID_ATTR = 'data-id';

/**
 * The id the node was loaded as. Equal to `ID_ATTR` once the backend has registered the node; still
 * pointing at the node it was copied from until then. Not staleness - a state machine's input.
 */
export const LOADED_ID_ATTR = 'data-loaded-id';

/**
 * False only while the attribute is present AND disagrees with `data-id` - a node whose registration
 * has not settled, so nothing can resolve it yet.
 *
 * An absent attribute means loaded. Html that predates the attribute, and every hand-built fixture,
 * has none, and treating those as unresolvable would silently blank content that renders today.
 */
export const isNodeLoaded = (attrs: Readonly<Record<string, string>>): boolean => {
  const loadedId = attrs[LOADED_ID_ATTR];
  return loadedId === undefined || loadedId === attrs[ID_ATTR];
};

// ---------------------------------------------------------------------------
// Element classification
// ---------------------------------------------------------------------------

/**
 * Atom: displayed text is NOT in the source HTML (runtime-resolved or binary).
 * Compared by attributes only - no child diff.
 */
export const ATOM_TAGS = new Set([
  'img',
  'ref',
  'inline-block',
  'reusable-block',
  'changes-math',
  'br',
  'literature-citation',
]);

/**
 * Inline container: displayed text IS in the source HTML as child text nodes.
 * Children are flattened into tokens and diffed word-by-word.
 * `li` excluded - in TipTap it always contains block children (`<p>`, nested lists).
 */
export const INLINE_CONTAINER_TAGS = new Set(['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'caption-node']);

/** Structural table wrappers - matched by type, not by id. */
export const TABLE_WRAPPER_TAGS = new Set(['table', 'tbody', 'thead']);

/** Table cells - allowed to match across type change (td ↔ th). */
export const TABLE_CELL_TAGS = new Set(['td', 'th']);

export const TABLE_ROW_TAG = 'tr';

/** All heading tags share a single semantic type for matching purposes. */
export const HEADING_TAGS = new Set(['h1', 'h2', 'h3', 'h4', 'h5']);

/** The root element tag returned by the parser (cheerio wraps content in <body>). */
export const ROOT_TAG = 'body';

/** Landscape section wrapper - transparent for matching, preserved in output. */
export const SECTION_TAG = 'section';
const SECTION_CLASS = 'document-section';
export const isDocumentSection = (el: { tagName: string; attribs?: Record<string, string> }): boolean =>
  el.tagName === SECTION_TAG && (el.attribs?.class || '').split(/\s+/).includes(SECTION_CLASS);

// ---------------------------------------------------------------------------
// Type normalization (for matching)
// ---------------------------------------------------------------------------

/**
 * Returns a semantic type key used by the matcher.
 *
 * h1–h5 all map to `'heading'` so the matcher treats a level change
 * (e.g. h2 → h3) as a modification rather than delete + insert.
 */
export const getSemanticType = (tagName: string): string => {
  if (HEADING_TAGS.has(tagName)) return 'heading';
  return tagName;
};

// ---------------------------------------------------------------------------
// Mark rules (for inline flattening)
// ---------------------------------------------------------------------------

export type DiffMark = {
  readonly name: string;
  readonly attrs: Readonly<Record<string, unknown>>;
};

type MarkRule = (attribs: Readonly<Record<string, string>>) => DiffMark | null;

/**
 * Maps HTML tag names to mark constructors.
 *
 * During inline flattening the walker checks each element against this table.
 * If a rule returns a `DiffMark`, the element is treated as a mark wrapper
 * and its children inherit that mark.  If it returns `null`, the element is
 * walked through transparently (e.g. an unknown `<span>`).
 */
export const MARK_RULES: Readonly<Record<string, MarkRule>> = {
  strong: () => ({ name: 'bold', attrs: {} }),
  b: () => ({ name: 'bold', attrs: {} }),
  em: () => ({ name: 'italic', attrs: {} }),
  i: () => ({ name: 'italic', attrs: {} }),
  u: () => ({ name: 'underline', attrs: {} }),
  s: () => ({ name: 'strike', attrs: {} }),
  code: () => ({ name: 'code', attrs: {} }),
  sup: () => ({ name: 'superscript', attrs: {} }),
  sub: () => ({ name: 'subscript', attrs: {} }),
  a: (a) => ({
    name: 'link',
    attrs: { href: a.href },
  }),
  mark: (a) => ({
    name: 'highlight',
    attrs: { color: a['data-color'], class: a.class },
  }),
  span: (a) => {
    if (a['data-custom-style']) {
      return { name: 'customStyleMark', attrs: { customStyleKey: a['data-custom-style'] } };
    }
    // Unknown span - return null so the walker treats it as transparent.
    return null;
  },
};

// ---------------------------------------------------------------------------
// Volatile attributes (ignored during comparison)
// ---------------------------------------------------------------------------

/**
 * Attributes that are derived or runtime-only and should not by themselves
 * produce a diff.  Keyed by HTML tag name.
 */
export const VOLATILE_ATTRS: Readonly<Record<string, readonly string[]>> = {
  'caption-node': ['labelnonce'],
  img: ['file', 'style', 'wrap', 'alt'],
  h1: ['numbering'],
  h2: ['numbering'],
  h3: ['numbering'],
  h4: ['numbering'],
  h5: ['numbering'],
  ref: ['loadedid'],
  'inline-block': ['loadedid'],
  'reusable-block': ['loadedid'],
  // Stable-id citation index is derived from (literatureId, insertion order);
  // muting it keeps version diffs quiet when a new citation inserted earlier
  // in the document renumbers every downstream chip.
  'literature-citation': ['data-index'],
  // Visual / layout attributes - not content changes
  table: ['data-transparent', 'data-border-style', 'data-border-weight', 'style'],
  td: [
    'style',
    'data-background-color',
    'data-offset-cell',
    'colwidth',
    'data-border-top-style',
    'data-border-right-style',
    'data-border-bottom-style',
    'data-border-left-style',
    'data-border-top-weight',
    'data-border-right-weight',
    'data-border-bottom-weight',
    'data-border-left-weight',
    'data-text-rotation',
    'data-vertical-align',
  ],
  th: [
    'style',
    'data-background-color',
    'data-offset-cell',
    'colwidth',
    'data-border-top-style',
    'data-border-right-style',
    'data-border-bottom-style',
    'data-border-left-style',
    'data-border-top-weight',
    'data-border-right-weight',
    'data-border-bottom-weight',
    'data-border-left-weight',
    'data-text-rotation',
    'data-vertical-align',
  ],
  ol: ['style', 'data-list-id', 'data-list-pattern'],
  ul: ['style', 'data-list-id', 'data-list-pattern'],
};

// ---------------------------------------------------------------------------
// Classification helpers
// ---------------------------------------------------------------------------
//
// Three levels of specificity:
//   Tag-level (differ dispatcher):     isAtom(tag), isInlineContainer(tag)
//   Element-level (differ + matcher):  isElementAtom(el), isElementInlineContainer(el)
//   Token-level (inline flattener):    isInlineAtomToken(el) - ATOM + INLINE_CONTAINER classes

export const isAtom = (tagName: string): boolean => ATOM_TAGS.has(tagName);

/** Class-based atoms: no displayed text in source HTML (visual spacers, decorative). */
const ATOM_CLASSES = [TAB_NODE_NAME];

/** Class-based inline containers: displayed text in source HTML, diffed word-by-word.
 *  Matched as a single token by the parent, but children are diffed inline when compared. */
const INLINE_CONTAINER_CLASSES = [
  ...TEMPLATE_FIELD_KINDS.filter((kind) => !kind.block).map((kind) => templateFieldWrapperClass(kind.name)),
  'import-warning',
];

const hasAnyClass = (el: { attribs?: Record<string, string> }, classes: readonly string[]): boolean => {
  const classList = (el.attribs?.class || '').split(/\s+/);
  return classes.some((c) => classList.includes(c));
};

/** Element-level atom check - recognizes both tag-based and class-based atoms. */
export const isElementAtom = (el: { tagName: string; attribs?: Record<string, string> }): boolean => {
  if (ATOM_TAGS.has(el.tagName)) return true;
  return hasAnyClass(el, ATOM_CLASSES);
};

export const isInlineContainer = (tagName: string): boolean => INLINE_CONTAINER_TAGS.has(tagName);

/** Element-level inline container check - includes class-based containers like template-field-inline. */
export const isElementInlineContainer = (el: { tagName: string; attribs?: Record<string, string> }): boolean => {
  if (INLINE_CONTAINER_TAGS.has(el.tagName)) return true;
  return hasAnyClass(el, INLINE_CONTAINER_CLASSES);
};

/**
 * All class-based elements that the inline tokenizer should treat as
 * indivisible atom tokens. Combines ATOM_CLASSES and INLINE_CONTAINER_CLASSES
 * - but NOT tag-based inline container sets (p, h1, etc.), since those are
 * already handled at the differ dispatcher level.
 */
const INLINE_ATOM_TOKEN_CLASSES = [...ATOM_CLASSES, ...INLINE_CONTAINER_CLASSES];

/** Check for inline atom tokens (tag-based atoms + class-based special elements). */
export const isInlineAtomToken = (el: { tagName: string; attribs?: Record<string, string> }): boolean => {
  if (ATOM_TAGS.has(el.tagName)) return true;
  return hasAnyClass(el, INLINE_ATOM_TOKEN_CLASSES);
};

export const isTableElement = (tagName: string): boolean =>
  TABLE_WRAPPER_TAGS.has(tagName) || TABLE_CELL_TAGS.has(tagName) || tagName === TABLE_ROW_TAG;

/** Returns true for elements that appear inside inline containers (marks and atoms). */
export const isInlineElement = (tagName: string): boolean => isAtom(tagName) || tagName in MARK_RULES;

// ---------------------------------------------------------------------------
// Attribute extraction
// ---------------------------------------------------------------------------

/** Shallow-copies the element's attributes into a plain object. */
export const getAttrs = (el: Element): Record<string, string> => ({ ...el.attribs });

/** Returns the stable identity attribute, or undefined. Empty strings are treated as "no ID". */
export const getElementId = (el: Element): string | undefined => el.attribs?.[ID_ATTR] || undefined;
